/**
 * SalesModule — Phase 2 UI for the B2B Sales System.
 *
 * Lives as a "Sales" tab inside each division's DivisionDetail page.
 * Scope: only that division. For CEO cross-division view, pass divisionId=null.
 *
 * Inner tabs:
 *   1. Dashboard  — per-rep activity (7d), pipeline health, funnel, targets
 *   2. Kanban     — 10 stage columns with deal cards, click to open detail modal
 *   3. All Leads  — flat table with filters
 *   4. Targets    — upsert FY targets per person
 *
 * Lead detail modal (LeadDetailDrawer):
 *   - Lead summary (editable stage dropdown, deal_value, expected_close)
 *   - Contacts tab — add/edit/delete; DM/Budget/Champion flag toggles
 *   - Activity log tab — log new call/email/meeting/WA/proposal_sent with next-action
 *   - Stage history tab — audit trail
 */
import { useEffect, useMemo, useState } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import {
  Briefcase, Users, Activity, History, Target, Plus, X, Star, TrendingUp,
  AlertTriangle, Phone, Mail, MessageCircle, Linkedin, FileText, CheckCircle,
} from 'lucide-react';

const INR = (n) => `₹${Math.round(n || 0).toLocaleString('en-IN')}`;
const LAKH = (n) => {
  n = n || 0;
  if (n >= 1e7) return `₹${(n / 1e7).toFixed(2)}Cr`;
  if (n >= 1e5) return `₹${(n / 1e5).toFixed(2)}L`;
  return INR(n);
};

const STAGE_COLORS = {
  lead:                '#9CA3AF',
  first_meeting_done:  '#6B7FB5',
  needs_identified:    '#5E8BB5',
  proposal_sent:       '#BA7517',
  proposal_discussed:  '#D4974A',
  negotiating:         '#7B5EA7',
  verbal_commit:       '#2A7B88',
  contract_signed:     '#2D8B5E',
  on_hold:             '#9F6B2D',
  lost:                '#D4726A',
};

export default function SalesModule({ divisionId, divisionName, isCEO = false }) {
  const [tab, setTab] = useState('dashboard');
  const [stages, setStages] = useState([]);
  const [meta, setMeta] = useState({ activity_types: [], lost_reasons: [] });
  const [selectedLead, setSelectedLead] = useState(null);
  const [showNewLead, setShowNewLead] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    api.get('/sales/stages').then(r => {
      setStages(r.data.stages || []);
      setMeta({ activity_types: r.data.activity_types || [], lost_reasons: r.data.lost_reasons || [] });
    });
  }, []);

  const reload = () => setRefreshKey(k => k + 1);

  return (
    <div className="space-y-4" data-testid="sales-module">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Briefcase size={18} style={{ color: '#2A7B88' }} />
          <div>
            <h2 className="text-base sm:text-lg font-bold" style={{ color: '#1B2A4A' }}>
              Sales System · {divisionName || 'All Divisions'}
            </h2>
            <p className="text-[11px]" style={{ color: '#6B7280' }}>
              Lead capture · contacts · activity log · 10-stage pipeline · targets
            </p>
          </div>
        </div>
        <button onClick={() => setShowNewLead(true)} data-testid="sales-new-lead-btn"
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold text-white"
          style={{ backgroundColor: '#2A7B88' }}>
          <Plus size={14} /> New Lead
        </button>
      </div>

      {/* Inner tabs */}
      <div className="flex gap-1 border-b" style={{ borderColor: '#E5E1DC' }}>
        {[
          { id: 'dashboard', label: 'Dashboard' },
          { id: 'kanban',    label: 'Kanban' },
          { id: 'leads',     label: 'All Leads' },
          { id: 'targets',   label: 'Targets' },
        ].map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} data-testid={`sales-tab-${t.id}`}
            className="px-3 py-1.5 text-xs font-semibold"
            style={{
              color: tab === t.id ? '#1B2A4A' : '#6B7280',
              borderBottom: tab === t.id ? '2px solid #2A7B88' : '2px solid transparent',
            }}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'dashboard' && <DashboardView divisionId={divisionId} refreshKey={refreshKey} />}
      {tab === 'kanban'    && <KanbanView divisionId={divisionId} stages={stages} onOpen={setSelectedLead} refreshKey={refreshKey} />}
      {tab === 'leads'     && <AllLeadsView divisionId={divisionId} stages={stages} onOpen={setSelectedLead} refreshKey={refreshKey} />}
      {tab === 'targets'   && <TargetsView divisionId={divisionId} onChange={reload} />}

      {selectedLead && (
        <LeadDetailDrawer leadId={selectedLead} stages={stages} meta={meta}
          onClose={() => { setSelectedLead(null); reload(); }} />
      )}
      {showNewLead && (
        <NewLeadModal divisionId={divisionId} stages={stages}
          onClose={() => setShowNewLead(false)}
          onCreated={(id) => { setShowNewLead(false); setSelectedLead(id); reload(); }} />
      )}
    </div>
  );
}

// -------------------- Dashboard View --------------------
function DashboardView({ divisionId, refreshKey }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const url = divisionId ? `/sales/dashboard/weekly?division_id=${divisionId}` : '/sales/dashboard/weekly';
    api.get(url).then(r => setData(r.data)).finally(() => setLoading(false));
  }, [divisionId, refreshKey]);

  if (loading) return <div className="p-6 text-center text-sm text-[#6B7280]">Loading…</div>;
  if (!data) return null;

  const ph = data.pipeline_health;
  const f = data.funnel_quarter;

  return (
    <div className="space-y-4" data-testid="sales-dashboard">
      {/* Per-rep activity */}
      <Card title="This Week's Sales Activity" subtitle="Last 7 days — meetings, calls, emails, WhatsApp">
        {data.per_rep.length === 0 ? (
          <Empty text="No activity logged in the last 7 days." />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs" data-testid="sales-per-rep-table">
              <thead>
                <tr style={{ backgroundColor: '#F7F5F2' }} className="text-left">
                  <th className="px-3 py-2 font-semibold">Rep</th>
                  <th className="px-2 py-2 font-semibold text-right">Meetings</th>
                  <th className="px-2 py-2 font-semibold text-right">Calls</th>
                  <th className="px-2 py-2 font-semibold text-right">Emails</th>
                  <th className="px-2 py-2 font-semibold text-right">WA</th>
                  <th className="px-2 py-2 font-semibold text-right">Proposals</th>
                  <th className="px-3 py-2 font-semibold text-right">Open Pipeline</th>
                  <th className="px-3 py-2 font-semibold text-right">Closed (wk)</th>
                </tr>
              </thead>
              <tbody>
                {data.per_rep.map((r, i) => (
                  <tr key={i} className="border-t" style={{ borderColor: '#F3F4F6' }} data-testid={`rep-row-${i}`}>
                    <td className="px-3 py-2 font-semibold" style={{ color: '#1B2A4A' }}>{r.name}</td>
                    <td className="px-2 py-2 text-right">{r.meetings}</td>
                    <td className="px-2 py-2 text-right">{r.calls}</td>
                    <td className="px-2 py-2 text-right">{r.emails}</td>
                    <td className="px-2 py-2 text-right">{r.whatsapps}</td>
                    <td className="px-2 py-2 text-right">{r.proposals_sent}</td>
                    <td className="px-3 py-2 text-right font-bold" style={{ color: '#2A7B88' }}>{LAKH(r.pipeline_value)}</td>
                    <td className="px-3 py-2 text-right" style={{ color: r.closed_this_week_value > 0 ? '#2D8B5E' : '#9CA3AF' }}>
                      {r.closed_this_week_count > 0 ? `${r.closed_this_week_count} · ${LAKH(r.closed_this_week_value)}` : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Pipeline health */}
      <div className="grid md:grid-cols-3 gap-3">
        <Stat label="Open Pipeline" value={LAKH(ph.total_pipeline_value)} accent="#2A7B88"
              sub={`${LAKH(ph.weighted_pipeline_value)} weighted`} testid="stat-pipeline" />
        <Stat label="Stuck Deals" value={ph.stuck_deals.length}
              accent={ph.stuck_deals.length > 0 ? '#D4726A' : '#9CA3AF'}
              sub="Past red-flag threshold" testid="stat-stuck" />
        <Stat label="Closing This Month" value={ph.closing_this_month.length}
              accent="#BA7517" sub={LAKH(ph.closing_this_month.reduce((s, d) => s + (d.deal_value || 0), 0))}
              testid="stat-closing" />
      </div>

      {ph.stuck_deals.length > 0 && (
        <Card title={<span className="flex items-center gap-1.5"><AlertTriangle size={14} style={{ color: '#D4726A' }} /> Deals Stuck in Stage</span>}
              subtitle="Past the expected-duration red flag">
          <SmallDealTable rows={ph.stuck_deals.map(d => ({ ...d, flag_col: `${d.days_in_stage}d` }))} testid="stuck-list" />
        </Card>
      )}

      {ph.no_response_proposals.length > 0 && (
        <Card title={<span className="flex items-center gap-1.5"><FileText size={14} style={{ color: '#BA7517' }} /> Proposals Sent · No Response {'>'}7 days</span>}>
          <SmallDealTable rows={ph.no_response_proposals.map(d => ({ ...d, flag_col: `${d.days_since_sent}d` }))} testid="no-response-list" />
        </Card>
      )}

      {ph.closing_this_month.length > 0 && (
        <Card title="Closing This Month">
          <SmallDealTable rows={ph.closing_this_month.map(d => ({ ...d, flag_col: `${d.probability}%` }))} testid="closing-list" />
        </Card>
      )}

      {/* Funnel */}
      <Card title={`Conversion Funnel · This Quarter · ${f.total_leads} leads created`} subtitle="Counts how many leads reached each stage (via stage history)">
        {f.total_leads === 0 ? (
          <Empty text="No leads created this quarter yet." />
        ) : (
          <div className="space-y-1.5" data-testid="funnel-view">
            {['lead','first_meeting_done','needs_identified','proposal_sent','proposal_discussed','negotiating','verbal_commit','contract_signed'].map(k => {
              const n = f.counts_reached[k] || 0;
              const pct = Math.min(100, (n / Math.max(f.total_leads, 1)) * 100);
              return (
                <div key={k} className="flex items-center gap-2 text-[11px]">
                  <span className="w-36 shrink-0" style={{ color: '#6B7280' }}>{k.replace(/_/g, ' ')}</span>
                  <div className="flex-1 h-4 rounded relative" style={{ backgroundColor: '#F3F4F6' }}>
                    <div className="h-4 rounded" style={{ width: `${pct}%`, backgroundColor: STAGE_COLORS[k] }} />
                    <span className="absolute inset-0 px-2 flex items-center font-semibold" style={{ color: pct > 20 ? 'white' : '#1B2A4A' }}>
                      {n} · {pct.toFixed(0)}%
                    </span>
                  </div>
                </div>
              );
            })}
            <div className="mt-3 grid grid-cols-3 gap-2 text-[11px]">
              <MiniKpi label="Lead → Meeting" val={`${f.conversions.lead_to_first_meeting_pct}%`} />
              <MiniKpi label="Lead → Proposal"  val={`${f.conversions.lead_to_proposal_pct}%`} />
              <MiniKpi label="Lead → Won" val={`${f.conversions.lead_to_won_pct}%`} />
            </div>
          </div>
        )}
      </Card>

      {/* Targets */}
      <Card title="Targets · FY26-27" subtitle="Per-person target vs FYTD won vs open pipeline">
        {data.targets.length === 0 ? (
          <Empty text="No targets set. Use the Targets tab to add them." />
        ) : (
          <div className="space-y-2" data-testid="targets-panel">
            {data.targets.map((t, i) => {
              const pct = Math.min(100, t.pct_achieved);
              return (
                <div key={i} className="p-3 rounded-md border" style={{ borderColor: '#E5E1DC' }} data-testid={`target-row-${i}`}>
                  <div className="flex items-start justify-between flex-wrap gap-2 mb-1">
                    <div>
                      <p className="text-sm font-semibold" style={{ color: '#1B2A4A' }}>{t.person_name}</p>
                      <p className="text-[10px]" style={{ color: '#9CA3AF' }}>{t.division_id?.toUpperCase()} · target {LAKH(t.target_amount)}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-bold" style={{ color: pct >= 75 ? '#2D8B5E' : pct >= 40 ? '#BA7517' : '#D4726A' }}>
                        {pct}%
                      </p>
                      <p className="text-[10px]" style={{ color: '#9CA3AF' }}>
                        {LAKH(t.won_amount_fytd)} won · gap {LAKH(t.gap_to_target)}
                      </p>
                    </div>
                  </div>
                  <div className="h-2 rounded" style={{ backgroundColor: '#F3F4F6' }}>
                    <div className="h-2 rounded" style={{ width: `${pct}%`, backgroundColor: pct >= 75 ? '#2D8B5E' : pct >= 40 ? '#BA7517' : '#D4726A' }} />
                  </div>
                  <p className="text-[10px] mt-1" style={{ color: '#6B7280' }}>Open pipeline: {LAKH(t.pipeline_open)}</p>
                </div>
              );
            })}
          </div>
        )}
      </Card>
    </div>
  );
}

// -------------------- Kanban View --------------------
function KanbanView({ divisionId, stages, onOpen, refreshKey }) {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const url = divisionId
      ? `/sales/leads?division_id=${divisionId}&include_closed=true`
      : '/sales/leads?include_closed=true';
    api.get(url).then(r => setLeads(r.data.leads || [])).finally(() => setLoading(false));
  }, [divisionId, refreshKey]);

  const cols = useMemo(() => stages.filter(s => s.key !== 'lost'), [stages]);
  const grouped = useMemo(() => {
    const g = {};
    for (const s of cols) g[s.key] = [];
    for (const l of leads) {
      const k = l.stage_key || 'lead';
      if (g[k]) g[k].push(l);
    }
    return g;
  }, [leads, cols]);

  if (loading) return <div className="p-6 text-center text-sm text-[#6B7280]">Loading…</div>;

  return (
    <div className="overflow-x-auto pb-2" data-testid="kanban-view">
      <div className="flex gap-3" style={{ minWidth: 1200 }}>
        {cols.map(col => (
          <div key={col.key} className="w-60 shrink-0 rounded-lg p-2" style={{ backgroundColor: '#F7F5F2' }}>
            <div className="flex items-center justify-between mb-2 px-1">
              <p className="text-[11px] font-bold" style={{ color: STAGE_COLORS[col.key] }}>{col.label}</p>
              <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded" style={{ backgroundColor: STAGE_COLORS[col.key], color: 'white' }}>
                {grouped[col.key]?.length || 0}
              </span>
            </div>
            <div className="space-y-1.5 max-h-[600px] overflow-y-auto">
              {(grouped[col.key] || []).map((l, i) => (
                <div key={l.id || l.client_company} onClick={() => onOpen(l.id || l.client_company)}
                  className="p-2 bg-white rounded-md shadow-sm cursor-pointer hover:shadow-md transition-shadow"
                  data-testid={`kanban-card-${col.key}-${i}`}>
                  <p className="text-[12px] font-semibold truncate" style={{ color: '#1B2A4A' }}>{l.client_company}</p>
                  <div className="flex justify-between mt-1 text-[10px]" style={{ color: '#6B7280' }}>
                    <span>{l.owner || 'Unassigned'}</span>
                    <span className="font-bold" style={{ color: '#2A7B88' }}>{LAKH(l.deal_value)}</span>
                  </div>
                  {l.is_stuck && (
                    <p className="text-[9px] mt-1 font-semibold" style={{ color: '#D4726A' }}>
                      ⚠ Stuck {l.days_in_stage}d
                    </p>
                  )}
                </div>
              ))}
              {(grouped[col.key]?.length || 0) === 0 && (
                <p className="text-[10px] text-center py-4" style={{ color: '#9CA3AF' }}>—</p>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// -------------------- All Leads Table --------------------
function AllLeadsView({ divisionId, stages, onOpen, refreshKey }) {
  const [leads, setLeads] = useState([]);
  const [stageFilter, setStageFilter] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    let url = '/sales/leads?include_closed=true';
    if (divisionId) url += `&division_id=${divisionId}`;
    if (stageFilter) url += `&stage=${stageFilter}`;
    api.get(url).then(r => setLeads(r.data.leads || [])).finally(() => setLoading(false));
  }, [divisionId, stageFilter, refreshKey]);

  return (
    <div className="space-y-2" data-testid="all-leads-view">
      <div className="flex items-center gap-2 text-xs">
        <label style={{ color: '#6B7280' }}>Filter stage:</label>
        <select value={stageFilter} onChange={e => setStageFilter(e.target.value)}
          data-testid="stage-filter"
          className="px-2 py-1 border rounded-md" style={{ borderColor: '#E5E1DC' }}>
          <option value="">All</option>
          {stages.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
        </select>
        <span style={{ color: '#9CA3AF' }}>· {leads.length} leads</span>
      </div>
      {loading ? <div className="p-4 text-center text-sm text-[#9CA3AF]">Loading…</div> : (
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr style={{ backgroundColor: '#F7F5F2' }} className="text-left">
                <th className="px-3 py-2 font-semibold">Company</th>
                <th className="px-3 py-2 font-semibold">Stage</th>
                <th className="px-3 py-2 font-semibold">Owner</th>
                <th className="px-3 py-2 font-semibold text-right">Value</th>
                <th className="px-3 py-2 font-semibold text-right">Days in Stage</th>
                <th className="px-3 py-2 font-semibold">Source</th>
              </tr>
            </thead>
            <tbody>
              {leads.map((l, i) => (
                <tr key={l.id || i} className="border-t cursor-pointer hover:bg-[#FAFAF7]"
                    style={{ borderColor: '#F3F4F6' }}
                    onClick={() => onOpen(l.id || l.client_company)}
                    data-testid={`all-lead-row-${i}`}>
                  <td className="px-3 py-2 font-semibold" style={{ color: '#1B2A4A' }}>{l.client_company}</td>
                  <td className="px-3 py-2">
                    <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full" style={{ backgroundColor: STAGE_COLORS[l.stage_key] + '30', color: STAGE_COLORS[l.stage_key] }}>
                      {l.stage_meta?.label || l.stage}
                    </span>
                  </td>
                  <td className="px-3 py-2">{l.owner}</td>
                  <td className="px-3 py-2 text-right font-semibold" style={{ color: '#2A7B88' }}>{LAKH(l.deal_value)}</td>
                  <td className="px-3 py-2 text-right" style={{ color: l.is_stuck ? '#D4726A' : '#6B7280' }}>
                    {l.days_in_stage}{l.is_stuck ? ' ⚠' : ''}
                  </td>
                  <td className="px-3 py-2" style={{ color: '#6B7280' }}>{l.source || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// -------------------- Targets View --------------------
function TargetsView({ divisionId, onChange }) {
  const [targets, setTargets] = useState([]);
  const [people, setPeople] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [form, setForm] = useState({ person_name: '', fy: 'FY26-27', target_amount: '', division_id: divisionId || '', outlet_scope: '' });

  const load = () => api.get('/sales/targets?fy=FY26-27').then(r => setTargets(r.data.targets || []));
  useEffect(() => {
    load();
    // Load people for the autocomplete dropdown — scope to division if provided
    const url = divisionId ? `/people?division_id=${divisionId}` : '/people';
    api.get(url).then(r => {
      const list = Array.isArray(r.data) ? r.data : (r.data?.people || r.data?.rows || []);
      setPeople(list);
    }).catch(() => setPeople([]));
  }, [divisionId]);

  const submit = async () => {
    if (!form.person_name || !form.target_amount) { toast.error('Name + amount required'); return; }
    await api.post('/sales/targets', { ...form, target_amount: Number(form.target_amount) });
    toast.success('Target saved');
    setShowNew(false); setForm({ person_name: '', fy: 'FY26-27', target_amount: '', division_id: divisionId || '', outlet_scope: '' });
    load(); onChange();
  };

  return (
    <div className="space-y-2" data-testid="targets-view">
      <div className="flex justify-end">
        <button onClick={() => setShowNew(true)} className="text-xs px-3 py-1.5 rounded-md text-white font-semibold"
          style={{ backgroundColor: '#2A7B88' }} data-testid="targets-add-btn">
          <Plus size={12} className="inline" /> Add / Update Target
        </button>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr style={{ backgroundColor: '#F7F5F2' }} className="text-left">
              <th className="px-3 py-2 font-semibold">Person</th>
              <th className="px-3 py-2 font-semibold">FY</th>
              <th className="px-3 py-2 font-semibold">Division</th>
              <th className="px-3 py-2 font-semibold">Scope</th>
              <th className="px-3 py-2 font-semibold text-right">Target</th>
            </tr>
          </thead>
          <tbody>
            {targets.map((t, i) => (
              <tr key={i} className="border-t" style={{ borderColor: '#F3F4F6' }}>
                <td className="px-3 py-2 font-semibold" style={{ color: '#1B2A4A' }}>{t.person_name}</td>
                <td className="px-3 py-2">{t.fy}</td>
                <td className="px-3 py-2">{t.division_id?.toUpperCase() || '—'}</td>
                <td className="px-3 py-2">{t.outlet_scope || '—'}</td>
                <td className="px-3 py-2 text-right font-bold" style={{ color: '#2A7B88' }}>{LAKH(t.target_amount)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {showNew && (
        <Modal onClose={() => setShowNew(false)} title="Add / Update Target">
          <div className="space-y-2">
            <PersonAutocomplete label="Person" value={form.person_name} onChange={v => setForm({ ...form, person_name: v })} people={people} testid="target-person" />
            <Input label="FY" value={form.fy} onChange={v => setForm({ ...form, fy: v })} testid="target-fy" />
            <Input label="Target Amount (₹)" type="number" value={form.target_amount} onChange={v => setForm({ ...form, target_amount: v })} testid="target-amount" />
            <Input label="Division ID (optional)" value={form.division_id} onChange={v => setForm({ ...form, division_id: v })} testid="target-div" />
            <Input label="Scope / Notes" value={form.outlet_scope} onChange={v => setForm({ ...form, outlet_scope: v })} testid="target-scope" />
            <div className="flex justify-end gap-2 pt-2">
              <button onClick={() => setShowNew(false)} className="text-xs px-3 py-1.5 rounded-md border" style={{ borderColor: '#E5E1DC' }}>Cancel</button>
              <button onClick={submit} className="text-xs px-3 py-1.5 rounded-md text-white font-semibold" style={{ backgroundColor: '#2A7B88' }} data-testid="target-save">Save</button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

// -------------------- New Lead Modal --------------------
function NewLeadModal({ divisionId, stages, onClose, onCreated }) {
  const [form, setForm] = useState({
    client_company: '', deal_name: '', division_id: divisionId || '',
    deal_value: '', source: '', source_detail: '', first_contact_date: '',
    brought_in_by: '', industry: '', company_size: '', owner: '', stage: 'lead', expected_close: '',
  });
  const submit = async () => {
    if (!form.client_company || !form.division_id) { toast.error('Company + division required'); return; }
    try {
      const r = await api.post('/sales/leads', { ...form, deal_value: Number(form.deal_value || 0) });
      toast.success('Lead created');
      onCreated(r.data.lead.id);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    }
  };
  return (
    <Modal onClose={onClose} title="New Lead">
      <div className="space-y-2" data-testid="new-lead-modal">
        <Input label="Company Name *" value={form.client_company} onChange={v => setForm({ ...form, client_company: v })} testid="lead-company" />
        <Input label="Deal Name" value={form.deal_name} onChange={v => setForm({ ...form, deal_name: v })} testid="lead-deal" />
        <div className="grid grid-cols-2 gap-2">
          <Input label="Division ID *" value={form.division_id} onChange={v => setForm({ ...form, division_id: v })} testid="lead-div" />
          <Input label="Deal Value (₹)" type="number" value={form.deal_value} onChange={v => setForm({ ...form, deal_value: v })} testid="lead-value" />
        </div>
        <div className="grid grid-cols-2 gap-2">
          <SelectInput label="Source" value={form.source} onChange={v => setForm({ ...form, source: v })}
            options={['Referral','Event','Website','LinkedIn','Cold','Cross-sell','Partner','Other']} testid="lead-source" />
          <Input label="Source Detail" value={form.source_detail} onChange={v => setForm({ ...form, source_detail: v })} testid="lead-source-detail" />
        </div>
        <div className="grid grid-cols-2 gap-2">
          <Input label="First Contact" type="date" value={form.first_contact_date} onChange={v => setForm({ ...form, first_contact_date: v })} testid="lead-first-contact" />
          <Input label="Brought in By" value={form.brought_in_by} onChange={v => setForm({ ...form, brought_in_by: v })} testid="lead-brought" />
        </div>
        <div className="grid grid-cols-2 gap-2">
          <Input label="Industry" value={form.industry} onChange={v => setForm({ ...form, industry: v })} testid="lead-industry" />
          <SelectInput label="Company Size" value={form.company_size} onChange={v => setForm({ ...form, company_size: v })}
            options={['1-10','11-50','51-200','201-1000','1000+']} testid="lead-size" />
        </div>
        <div className="grid grid-cols-2 gap-2">
          <Input label="Owner" value={form.owner} onChange={v => setForm({ ...form, owner: v })} testid="lead-owner" />
          <SelectInput label="Initial Stage" value={form.stage} onChange={v => setForm({ ...form, stage: v })}
            options={stages.map(s => ({ value: s.key, label: s.label }))} testid="lead-stage" />
        </div>
        <Input label="Expected Close (YYYY-MM-DD)" value={form.expected_close} onChange={v => setForm({ ...form, expected_close: v })} testid="lead-close" />
        <div className="flex justify-end gap-2 pt-2">
          <button onClick={onClose} className="text-xs px-3 py-1.5 rounded-md border" style={{ borderColor: '#E5E1DC' }}>Cancel</button>
          <button onClick={submit} className="text-xs px-3 py-1.5 rounded-md text-white font-semibold" style={{ backgroundColor: '#2A7B88' }} data-testid="lead-create-btn">Create</button>
        </div>
      </div>
    </Modal>
  );
}

// -------------------- Lead Detail Drawer --------------------
function LeadDetailDrawer({ leadId, stages, meta, onClose }) {
  const [data, setData] = useState(null);
  const [tab, setTab] = useState('summary');
  const [refresh, setRefresh] = useState(0);

  const load = () => api.get(`/sales/leads/${leadId}`).then(r => setData(r.data));
  useEffect(() => { load(); }, [leadId, refresh]);

  if (!data) return (
    <div className="fixed inset-0 z-50 flex items-center justify-center" style={{ backgroundColor: 'rgba(0,0,0,0.4)' }}>
      <div className="bg-white p-8 rounded-lg text-sm" style={{ color: '#6B7280' }}>Loading…</div>
    </div>
  );

  const L = data.lead;
  const updateStage = async (newKey) => {
    await api.patch(`/sales/leads/${L.id || L.client_company}`, { stage: newKey });
    toast.success(`Moved to ${stages.find(s => s.key === newKey)?.label || newKey}`);
    setRefresh(r => r + 1);
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end" style={{ backgroundColor: 'rgba(0,0,0,0.4)' }} onClick={onClose} data-testid="lead-detail-drawer">
      <div className="bg-white w-full max-w-2xl h-full overflow-y-auto shadow-xl" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="sticky top-0 bg-white border-b p-4 z-10" style={{ borderColor: '#E5E1DC' }}>
          <div className="flex items-start justify-between">
            <div>
              <h2 className="text-lg font-bold" style={{ color: '#1B2A4A' }} data-testid="lead-title">{L.client_company}</h2>
              <p className="text-[11px]" style={{ color: '#6B7280' }}>{L.deal_name} · {L.division_id?.toUpperCase()} · {L.owner}</p>
            </div>
            <button onClick={onClose} data-testid="lead-close-btn"><X size={18} style={{ color: '#6B7280' }} /></button>
          </div>
          {/* Stage chips */}
          <div className="mt-2 flex flex-wrap gap-1">
            {stages.filter(s => s.key !== 'lost').map(s => (
              <button key={s.key} onClick={() => updateStage(s.key)}
                data-testid={`stage-chip-${s.key}`}
                className="text-[10px] font-semibold px-2 py-0.5 rounded-full"
                style={{
                  backgroundColor: L.stage_key === s.key ? STAGE_COLORS[s.key] : '#F3F4F6',
                  color: L.stage_key === s.key ? 'white' : STAGE_COLORS[s.key],
                  border: L.stage_key === s.key ? 'none' : `1px solid ${STAGE_COLORS[s.key]}40`,
                }}>
                {s.label}
              </button>
            ))}
            <button onClick={() => updateStage('lost')} data-testid="stage-chip-lost"
              className="text-[10px] font-semibold px-2 py-0.5 rounded-full"
              style={{
                backgroundColor: L.stage_key === 'lost' ? '#D4726A' : '#F3F4F6',
                color: L.stage_key === 'lost' ? 'white' : '#D4726A',
              }}>Lost</button>
          </div>
          {/* Meta row */}
          <div className="grid grid-cols-3 gap-2 mt-3 text-[10px]">
            <MetaRow label="Value" value={LAKH(L.deal_value)} />
            <MetaRow label="Expected Close" value={L.expected_close || '—'} />
            <MetaRow label="Days in Stage" value={`${L.days_in_stage || 0}${L.is_stuck ? ' ⚠ STUCK' : ''}`} flag={L.is_stuck} />
            <MetaRow label="Source" value={L.source || '—'} />
            <MetaRow label="Industry" value={L.industry || '—'} />
            <MetaRow label="Company Size" value={L.company_size || '—'} />
          </div>
          {L.is_legacy_migration && (
            <div className="mt-2 px-2 py-1 rounded text-[10px] font-semibold inline-block"
                 style={{ backgroundColor: '#FEF3C7', color: '#92400E', border: '1px solid #F59E0B' }}
                 data-testid="legacy-migration-badge">
              LEGACY MIGRATION · Add real signing date to include this in funnel math
            </div>
          )}
        </div>

        {/* Inner tabs */}
        <div className="flex gap-1 border-b px-4" style={{ borderColor: '#E5E1DC' }}>
          {[
            { id: 'summary',  label: 'Summary' },
            { id: 'contacts', label: `Contacts (${data.contacts.length})` },
            { id: 'activity', label: `Activity (${data.activities.length})` },
            { id: 'history',  label: 'Stage History' },
          ].map(t => (
            <button key={t.id} onClick={() => setTab(t.id)} data-testid={`lead-inner-tab-${t.id}`}
              className="px-3 py-2 text-xs font-semibold"
              style={{ color: tab === t.id ? '#1B2A4A' : '#6B7280', borderBottom: tab === t.id ? '2px solid #2A7B88' : '2px solid transparent' }}>
              {t.label}
            </button>
          ))}
        </div>

        <div className="p-4 space-y-3">
          {tab === 'summary'  && <SummaryTab lead={L} onChange={() => setRefresh(r => r + 1)} lostReasons={meta.lost_reasons} />}
          {tab === 'contacts' && <ContactsTab leadId={L.id || L.client_company} contacts={data.contacts} onChange={() => setRefresh(r => r + 1)} />}
          {tab === 'activity' && <ActivityTab leadId={L.id || L.client_company} activities={data.activities} contacts={data.contacts} activityTypes={meta.activity_types} onChange={() => setRefresh(r => r + 1)} />}
          {tab === 'history'  && <HistoryTab history={L.stage_history || []} stages={stages} />}
        </div>
      </div>
    </div>
  );
}

// -------------------- Drawer Sub-Tabs --------------------
function SummaryTab({ lead, onChange, lostReasons }) {
  const [notes, setNotes] = useState(lead.notes || '');
  const [dealValue, setDealValue] = useState(lead.deal_value || 0);
  const [expectedClose, setExpectedClose] = useState(lead.expected_close || '');
  const [lostReason, setLostReason] = useState(lead.lost_reason || '');
  const [holdReason, setHoldReason] = useState(lead.hold_reason || '');
  const [actualClose, setActualClose] = useState(
    lead.actual_close_date ? String(lead.actual_close_date).slice(0, 10) : ''
  );
  const [isLegacy, setIsLegacy] = useState(!!lead.is_legacy_migration);

  const save = async () => {
    const patch = { notes, deal_value: Number(dealValue || 0), expected_close: expectedClose };
    if (lead.stage_key === 'lost') patch.lost_reason = lostReason;
    if (lead.stage_key === 'on_hold') patch.hold_reason = holdReason;
    if (lead.stage_key === 'contract_signed') {
      patch.actual_close_date = actualClose || null;
      patch.is_legacy_migration = isLegacy;
    }
    await api.patch(`/sales/leads/${lead.id || lead.client_company}`, patch);
    toast.success('Saved');
    onChange();
  };

  return (
    <div className="space-y-2" data-testid="summary-tab">
      <div className="grid grid-cols-2 gap-2">
        <Input label="Deal Value (₹)" type="number" value={dealValue} onChange={setDealValue} testid="summary-value" />
        <Input label="Expected Close" type="date" value={expectedClose} onChange={setExpectedClose} testid="summary-close" />
      </div>
      {lead.stage_key === 'contract_signed' && (
        <div className="p-2 rounded-md border" style={{ borderColor: '#E5E1DC', backgroundColor: '#FAFAFA' }}>
          <p className="text-[10px] uppercase font-semibold mb-1" style={{ color: '#2A7B88' }}>Contract Details</p>
          <div className="grid grid-cols-2 gap-2">
            <Input label="Actual Close Date" type="date" value={actualClose} onChange={setActualClose} testid="summary-actual-close" />
            <div className="flex items-end pb-1">
              <label className="flex items-center gap-1.5 text-[11px] cursor-pointer" style={{ color: '#1B2A4A' }}>
                <input type="checkbox" checked={isLegacy} onChange={e => setIsLegacy(e.target.checked)}
                  data-testid="summary-legacy-toggle" style={{ accentColor: '#F59E0B' }} />
                <span>Legacy Migration (exclude from funnel)</span>
              </label>
            </div>
          </div>
          {!actualClose && (
            <p className="text-[10px] mt-1" style={{ color: '#92400E' }}>
              Once you fill the real close date, uncheck "Legacy Migration" to include this deal in the quarterly funnel.
            </p>
          )}
        </div>
      )}
      {lead.stage_key === 'lost' && (
        <SelectInput label="Lost Reason" value={lostReason} onChange={setLostReason}
          options={lostReasons.map(r => ({ value: r, label: r.replace(/_/g, ' ') }))} testid="summary-lost-reason" />
      )}
      {lead.stage_key === 'on_hold' && (
        <Input label="Hold Reason" value={holdReason} onChange={setHoldReason} testid="summary-hold-reason" />
      )}
      <div>
        <label className="text-[10px] uppercase font-semibold block mb-0.5" style={{ color: '#6B7280' }}>Notes</label>
        <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={4}
          className="w-full text-xs px-2 py-1 border rounded-md" style={{ borderColor: '#E5E1DC' }}
          data-testid="summary-notes" />
      </div>
      <button onClick={save} className="text-xs px-3 py-1.5 rounded-md text-white font-semibold" style={{ backgroundColor: '#2A7B88' }} data-testid="summary-save">
        Save Changes
      </button>
    </div>
  );
}

function ContactsTab({ leadId, contacts, onChange }) {
  const [showNew, setShowNew] = useState(false);
  const [form, setForm] = useState({ name: '', designation: '', phone: '', email: '', linkedin: '', notes: '', is_decision_maker: false, is_budget_holder: false, is_champion: false });
  const add = async () => {
    if (!form.name) { toast.error('Name required'); return; }
    await api.post(`/sales/leads/${leadId}/contacts`, form);
    toast.success('Contact added');
    setShowNew(false); setForm({ name: '', designation: '', phone: '', email: '', linkedin: '', notes: '', is_decision_maker: false, is_budget_holder: false, is_champion: false });
    onChange();
  };
  const del = async (cid) => {
    if (!confirm('Delete this contact?')) return;
    await api.delete(`/contacts/${cid}`);
    onChange();
  };

  return (
    <div className="space-y-2" data-testid="contacts-tab">
      <div className="flex justify-end">
        <button onClick={() => setShowNew(true)} className="text-xs px-2 py-1 rounded text-white" style={{ backgroundColor: '#2A7B88' }} data-testid="contact-add-btn">
          <Plus size={12} className="inline" /> Add Contact
        </button>
      </div>
      {contacts.length === 0 && <Empty text="No contacts yet. Click 'Add Contact'." />}
      <div className="space-y-2">
        {contacts.map((c, i) => (
          <div key={c.id || i} className="p-3 rounded-md border" style={{ borderColor: '#E5E1DC' }} data-testid={`contact-${i}`}>
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm font-semibold" style={{ color: '#1B2A4A' }}>{c.name}</p>
                <p className="text-[11px]" style={{ color: '#6B7280' }}>{c.designation || '—'}</p>
                <div className="text-[10px] mt-1 space-y-0.5" style={{ color: '#6B7280' }}>
                  {c.phone && <p>📞 {c.phone}</p>}
                  {c.email && <p>✉️ {c.email}</p>}
                  {c.linkedin && <p>🔗 {c.linkedin}</p>}
                </div>
              </div>
              <div className="flex flex-col gap-1 items-end">
                <div className="flex gap-1 flex-wrap justify-end">
                  {c.is_decision_maker && <Pill color="#2A7B88">DM</Pill>}
                  {c.is_budget_holder   && <Pill color="#BA7517">BUDGET</Pill>}
                  {c.is_champion        && <Pill color="#7B5EA7"><Star size={8} className="inline" /> CHAMPION</Pill>}
                </div>
                <button onClick={() => del(c.id)} className="text-[10px]" style={{ color: '#D4726A' }} data-testid={`contact-del-${i}`}>Delete</button>
              </div>
            </div>
            {c.notes && <p className="text-[11px] mt-1" style={{ color: '#6B7280' }}>{c.notes}</p>}
          </div>
        ))}
      </div>
      {showNew && (
        <Modal onClose={() => setShowNew(false)} title="New Contact">
          <div className="space-y-2">
            <Input label="Name *" value={form.name} onChange={v => setForm({ ...form, name: v })} testid="contact-name" />
            <Input label="Designation" value={form.designation} onChange={v => setForm({ ...form, designation: v })} testid="contact-designation" />
            <div className="grid grid-cols-2 gap-2">
              <Input label="Phone" value={form.phone} onChange={v => setForm({ ...form, phone: v })} testid="contact-phone" />
              <Input label="Email" value={form.email} onChange={v => setForm({ ...form, email: v })} testid="contact-email" />
            </div>
            <Input label="LinkedIn" value={form.linkedin} onChange={v => setForm({ ...form, linkedin: v })} testid="contact-linkedin" />
            <Input label="Notes" value={form.notes} onChange={v => setForm({ ...form, notes: v })} testid="contact-notes" />
            <div className="flex gap-3 text-xs pt-1">
              <Checkbox label="Decision Maker" checked={form.is_decision_maker} onChange={v => setForm({ ...form, is_decision_maker: v })} testid="contact-dm" />
              <Checkbox label="Budget Holder" checked={form.is_budget_holder}   onChange={v => setForm({ ...form, is_budget_holder: v })} testid="contact-bh" />
              <Checkbox label="Champion" checked={form.is_champion}             onChange={v => setForm({ ...form, is_champion: v })} testid="contact-ch" />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <button onClick={() => setShowNew(false)} className="text-xs px-3 py-1.5 rounded-md border" style={{ borderColor: '#E5E1DC' }}>Cancel</button>
              <button onClick={add} className="text-xs px-3 py-1.5 rounded-md text-white font-semibold" style={{ backgroundColor: '#2A7B88' }} data-testid="contact-save">Save</button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

function ActivityTab({ leadId, activities, activityTypes, onChange }) {
  const [showNew, setShowNew] = useState(false);
  const [form, setForm] = useState({ type: 'call', summary: '', next_action: '', next_action_date: '', our_person: '' });
  const add = async () => {
    if (!form.summary) { toast.error('Summary required'); return; }
    await api.post(`/sales/leads/${leadId}/activities`, form);
    toast.success('Activity logged');
    setShowNew(false); setForm({ type: 'call', summary: '', next_action: '', next_action_date: '', our_person: '' });
    onChange();
  };

  const iconFor = (t) => {
    const map = { call: Phone, email: Mail, meeting: Users, whatsapp: MessageCircle, linkedin: Linkedin, note: FileText, proposal_sent: FileText, contract_sent: CheckCircle };
    const I = map[t] || Activity;
    return <I size={14} />;
  };

  return (
    <div className="space-y-2" data-testid="activity-tab">
      <div className="flex justify-end">
        <button onClick={() => setShowNew(true)} className="text-xs px-2 py-1 rounded text-white" style={{ backgroundColor: '#2A7B88' }} data-testid="activity-add-btn">
          <Plus size={12} className="inline" /> Log Activity
        </button>
      </div>
      {activities.length === 0 && <Empty text="No activity logged yet." />}
      <div className="space-y-1.5">
        {activities.map((a, i) => (
          <div key={a.id || i} className="p-2.5 rounded-md border flex gap-2" style={{ borderColor: '#E5E1DC' }} data-testid={`activity-${i}`}>
            <div className="shrink-0 w-7 h-7 rounded-full flex items-center justify-center" style={{ backgroundColor: '#F7F5F2', color: '#2A7B88' }}>
              {iconFor(a.type)}
            </div>
            <div className="flex-1">
              <div className="flex items-baseline justify-between flex-wrap gap-1">
                <p className="text-xs font-semibold capitalize" style={{ color: '#1B2A4A' }}>{a.type} · {a.our_person}</p>
                <p className="text-[10px]" style={{ color: '#9CA3AF' }}>{a.date?.slice(0, 16).replace('T', ' ')}</p>
              </div>
              <p className="text-[11px] mt-0.5" style={{ color: '#374151' }}>{a.summary}</p>
              {a.next_action && (
                <p className="text-[10px] mt-1" style={{ color: '#BA7517' }}>
                  → Next: {a.next_action}{a.next_action_date ? ` (by ${a.next_action_date.slice(0, 10)})` : ''}
                </p>
              )}
            </div>
          </div>
        ))}
      </div>
      {showNew && (
        <Modal onClose={() => setShowNew(false)} title="Log Activity">
          <div className="space-y-2">
            <SelectInput label="Type" value={form.type} onChange={v => setForm({ ...form, type: v })}
              options={activityTypes.map(t => ({ value: t, label: t.replace(/_/g, ' ') }))} testid="activity-type" />
            <Input label="Summary *" value={form.summary} onChange={v => setForm({ ...form, summary: v })} testid="activity-summary" />
            <Input label="Our Person" value={form.our_person} onChange={v => setForm({ ...form, our_person: v })} testid="activity-person" />
            <Input label="Next Action" value={form.next_action} onChange={v => setForm({ ...form, next_action: v })} testid="activity-next" />
            <Input label="Next Action Date" type="date" value={form.next_action_date} onChange={v => setForm({ ...form, next_action_date: v })} testid="activity-next-date" />
            <div className="flex justify-end gap-2 pt-2">
              <button onClick={() => setShowNew(false)} className="text-xs px-3 py-1.5 rounded-md border" style={{ borderColor: '#E5E1DC' }}>Cancel</button>
              <button onClick={add} className="text-xs px-3 py-1.5 rounded-md text-white font-semibold" style={{ backgroundColor: '#2A7B88' }} data-testid="activity-save">Save</button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

function HistoryTab({ history, stages }) {
  if (!history || history.length === 0) return <Empty text="No stage changes yet." />;
  const labelFor = (k) => stages.find(s => s.key === k)?.label || k;
  return (
    <div className="space-y-2" data-testid="history-tab">
      {history.map((h, i) => (
        <div key={i} className="flex items-center gap-2 text-xs" data-testid={`history-${i}`}>
          <History size={12} style={{ color: '#9CA3AF' }} />
          <span className="w-12 shrink-0" style={{ color: '#9CA3AF' }}>#{i + 1}</span>
          <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold" style={{ backgroundColor: STAGE_COLORS[h.stage] + '30', color: STAGE_COLORS[h.stage] }}>
            {labelFor(h.stage)}
          </span>
          <span style={{ color: '#6B7280' }}>{h.date?.slice(0, 16).replace('T', ' ')}</span>
          <span style={{ color: '#9CA3AF' }}>· by {h.by || '—'}</span>
        </div>
      ))}
    </div>
  );
}

// -------------------- Reusable Bits --------------------
function Card({ title, subtitle, children }) {
  return (
    <div className="rounded-lg border p-4" style={{ borderColor: '#E5E1DC', backgroundColor: '#FFFFFF' }}>
      <div className="mb-3">
        <h3 className="text-sm font-bold" style={{ color: '#1B2A4A' }}>{title}</h3>
        {subtitle && <p className="text-[11px]" style={{ color: '#9CA3AF' }}>{subtitle}</p>}
      </div>
      {children}
    </div>
  );
}
function Stat({ label, value, sub, accent, testid }) {
  return (
    <div className="rounded-lg border p-3" style={{ borderColor: '#E5E1DC', backgroundColor: '#FFFFFF' }} data-testid={testid}>
      <p className="text-[10px] font-semibold uppercase tracking-wide" style={{ color: '#6B7280' }}>{label}</p>
      <p className="text-2xl font-bold mt-1" style={{ color: accent }}>{value}</p>
      {sub && <p className="text-[10px] mt-1" style={{ color: '#9CA3AF' }}>{sub}</p>}
    </div>
  );
}
function MiniKpi({ label, val }) {
  return (
    <div className="p-2 rounded-md" style={{ backgroundColor: '#FAFAF7' }}>
      <p className="text-[9px] uppercase" style={{ color: '#9CA3AF' }}>{label}</p>
      <p className="text-sm font-bold" style={{ color: '#1B2A4A' }}>{val}</p>
    </div>
  );
}
function MetaRow({ label, value, flag }) {
  return (
    <div className="flex flex-col">
      <span className="text-[9px] uppercase" style={{ color: '#9CA3AF' }}>{label}</span>
      <span className="text-xs font-semibold" style={{ color: flag ? '#D4726A' : '#1B2A4A' }}>{value}</span>
    </div>
  );
}
function Pill({ color, children }) {
  return <span className="text-[9px] font-semibold px-1.5 py-0.5 rounded" style={{ backgroundColor: color + '20', color }}>{children}</span>;
}
function Empty({ text }) {
  return <p className="text-sm text-center py-6" style={{ color: '#9CA3AF' }}>{text}</p>;
}
function SmallDealTable({ rows, testid }) {
  return (
    <table className="w-full text-xs" data-testid={testid}>
      <thead>
        <tr style={{ backgroundColor: '#F7F5F2' }} className="text-left">
          <th className="px-2 py-1 font-semibold">Company</th>
          <th className="px-2 py-1 font-semibold">Stage</th>
          <th className="px-2 py-1 font-semibold">Owner</th>
          <th className="px-2 py-1 font-semibold text-right">Value</th>
          <th className="px-2 py-1 font-semibold text-right">Flag</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((d, i) => (
          <tr key={i} className="border-t" style={{ borderColor: '#F3F4F6' }}>
            <td className="px-2 py-1 font-semibold">{d.client_company}</td>
            <td className="px-2 py-1">{d.stage}</td>
            <td className="px-2 py-1">{d.owner}</td>
            <td className="px-2 py-1 text-right font-semibold" style={{ color: '#2A7B88' }}>{LAKH(d.deal_value)}</td>
            <td className="px-2 py-1 text-right font-bold" style={{ color: '#D4726A' }}>{d.flag_col}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
function Modal({ title, onClose, children }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center" style={{ backgroundColor: 'rgba(0,0,0,0.4)' }} onClick={onClose}>
      <div className="bg-white rounded-lg p-4 max-w-md w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="flex justify-between items-center mb-3">
          <h3 className="text-sm font-bold" style={{ color: '#1B2A4A' }}>{title}</h3>
          <button onClick={onClose}><X size={16} /></button>
        </div>
        {children}
      </div>
    </div>
  );
}
function Input({ label, value, onChange, type = 'text', testid }) {
  return (
    <div>
      <label className="text-[10px] uppercase font-semibold block mb-0.5" style={{ color: '#6B7280' }}>{label}</label>
      <input type={type} value={value} onChange={e => onChange(e.target.value)}
        className="w-full text-xs px-2 py-1 border rounded-md" style={{ borderColor: '#E5E1DC' }}
        data-testid={testid} />
    </div>
  );
}
function SelectInput({ label, value, onChange, options, testid }) {
  return (
    <div>
      <label className="text-[10px] uppercase font-semibold block mb-0.5" style={{ color: '#6B7280' }}>{label}</label>
      <select value={value} onChange={e => onChange(e.target.value)}
        className="w-full text-xs px-2 py-1 border rounded-md" style={{ borderColor: '#E5E1DC' }}
        data-testid={testid}>
        <option value="">—</option>
        {options.map(o => {
          const v = typeof o === 'string' ? o : o.value;
          const l = typeof o === 'string' ? o : o.label;
          return <option key={v} value={v}>{l}</option>;
        })}
      </select>
    </div>
  );
}
function Checkbox({ label, checked, onChange, testid }) {
  return (
    <label className="flex items-center gap-1 cursor-pointer">
      <input type="checkbox" checked={checked} onChange={e => onChange(e.target.checked)} data-testid={testid} />
      <span style={{ color: '#374151' }}>{label}</span>
    </label>
  );
}

function PersonAutocomplete({ label, value, onChange, people, testid }) {
  const [open, setOpen] = useState(false);
  const filtered = value
    ? people.filter(p => p.name?.toLowerCase().includes(value.toLowerCase())).slice(0, 8)
    : people.slice(0, 8);
  return (
    <div className="relative">
      <label className="text-[10px] uppercase font-semibold block mb-0.5" style={{ color: '#6B7280' }}>{label}</label>
      <input type="text" value={value} onChange={e => { onChange(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)} onBlur={() => setTimeout(() => setOpen(false), 150)}
        placeholder="Type name or pick from list…"
        className="w-full text-xs px-2 py-1 border rounded-md" style={{ borderColor: '#E5E1DC' }}
        data-testid={testid} />
      {open && filtered.length > 0 && (
        <div className="absolute z-10 mt-1 w-full bg-white border rounded-md shadow-md max-h-48 overflow-y-auto" style={{ borderColor: '#E5E1DC' }}>
          {filtered.map((p, i) => (
            <button key={p._id || p.id || i} type="button"
              onClick={() => { onChange(p.name); setOpen(false); }}
              className="block w-full text-left px-2 py-1 text-xs hover:bg-[#F7F5F2]"
              data-testid={`${testid}-opt-${i}`}>
              <span className="font-semibold" style={{ color: '#1B2A4A' }}>{p.name}</span>
              <span className="ml-2" style={{ color: '#9CA3AF' }}>{p.role || p.designation || ''}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
